/**
 * The InMail text with its links shown the way LinkedIn shows them (Sofia, Oct 22 2026): blue,
 * semibold words; hover shows the address; click opens a small box to change the words or the
 * address, or remove the link; select words and a "Link" button appears to make a new one.
 *
 * Stored exactly as before — plain text with `[words](https://…)` for a link — so the guardrails,
 * the ✨ rewrite, the drafts and the extension (which turns them into real LinkedIn links) all keep
 * reading the same string. The DOM half is the Email Library's (`linkEditorDom`), so both editors
 * write the same format. Uncontrolled like `LinkTemplateEditor`: the DOM is rebuilt only when the
 * value changes from outside, never on the recruiter's own keystrokes (that would move the caret).
 *
 * The tooltip, the "Link" button and the link box render into document.body at fixed viewport
 * coordinates (Sofia, Oct 27 2026: the box opened behind the card's `overflow-hidden` frame and could
 * not be closed). They follow their words on scroll/resize; Escape or a click outside closes the box.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { ALLOWED_HREF, makeLink, markPending, clearPending, renderInto, serialize, unwrapLink } from '../../components/linkEditorDom';

/** LinkedIn's link look: its blue, semibold, no underline — over the Library editor's anchor classes. */
const LINK_LOOK =
  '[&_a]:cursor-pointer [&_a]:rounded-none [&_a]:bg-transparent [&_a]:font-semibold [&_a]:text-[#0A66C2] [&_a]:no-underline hover:[&_a]:underline';

/** Viewport coordinates (`position: fixed`). */
type Box = { top: number; left: number };
/** The link box's width (`w-[320px]`), kept inside the window. */
const BOX_WIDTH = 320;
/** Just below a rect, in the viewport, never past the window's right edge for the box. */
const below = (r: DOMRect, width = 0): Box => {
  const right = (globalThis.innerWidth || 0) - width - 8;
  return { top: r.bottom + 4, left: Math.max(8, width && right > 8 ? Math.min(r.left, right) : r.left) };
};
/** Above the app's modals (design system: modal 1300, popover 10000). */
const LAYER = 'fixed z-popover';
/** The link box: what the fields show. The DOM node it is about lives in a ref (`target`). */
type Editing = { existing: boolean; text: string; href: string; at: Box };

/** "calendly.com/x" → "https://calendly.com/x"; anything that still is no https link → null. */
function normaliseHref(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
  return /^https:\/\/[^\s]+\.[^\s]+/i.test(withScheme) || /^mailto:\S+@\S+/i.test(withScheme) ? withScheme : null;
}

export function InlineLinkText({
  value,
  onChange,
  label,
  disabled,
  minRows = 4,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  disabled?: boolean;
  minRows?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const emitted = useRef<string | null>(null);
  const [hover, setHover] = useState<{ href: string; at: Box } | null>(null);
  const [selectionAt, setSelectionAt] = useState<Box | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The anchor being edited, or the highlighted words a new link is for. */
  const target = useRef<{ a: HTMLAnchorElement | null; pending: HTMLSpanElement | null }>({ a: null, pending: null });

  useLayoutEffect(() => {
    const el = root.current;
    if (!el || value === emitted.current) return;
    renderInto(el, value);
    emitted.current = value;
  }, [value]);

  const emit = () => {
    const el = root.current;
    if (!el) return;
    const next = serialize(el);
    emitted.current = next;
    onChange(next);
  };

  // The "Link" button follows a selection of words inside the text.
  useEffect(() => {
    const onSel = () => {
      const el = root.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0 || disabled) return setSelectionAt(null);
      const range = sel.getRangeAt(0);
      if (range.collapsed || !el.contains(range.commonAncestorContainer) || !range.toString().trim()) return setSelectionAt(null);
      const rect = range.getBoundingClientRect?.();
      setSelectionAt(rect ? below(rect) : { top: 8, left: 8 });
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, [disabled]);

  const openFor = (a: HTMLAnchorElement) => {
    setHover(null);
    setError(null);
    target.current = { a, pending: null };
    setEditing({ existing: true, text: a.textContent ?? '', href: a.getAttribute('data-href') || a.getAttribute('href') || '', at: below(a.getBoundingClientRect(), BOX_WIDTH) });
  };

  const onClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest?.('a');
    if (!a || !root.current?.contains(a)) return;
    // Never navigate away from the card, also when the text is locked (CodeRabbit, PR #681).
    e.preventDefault();
    if (!disabled) openFor(a as HTMLAnchorElement);
  };

  const onOver = (e: ReactMouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest?.('a');
    if (!a || editing) return setHover(null);
    setHover({ href: a.getAttribute('data-href') || a.getAttribute('href') || '', at: below(a.getBoundingClientRect()) });
  };

  const newLink = () => {
    const el = root.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed || !el.contains(range.commonAncestorContainer)) return;
    const rect = range.getBoundingClientRect?.();
    const pending = markPending(range, el);
    sel.removeAllRanges();
    setSelectionAt(null);
    setError(null);
    target.current = { a: null, pending };
    setEditing({ existing: false, text: pending.textContent ?? '', href: '', at: rect ? below(rect, BOX_WIDTH) : { top: 8, left: 8 } });
  };

  const close = () => {
    const { pending } = target.current;
    if (pending?.isConnected) clearPending(pending);
    target.current = { a: null, pending: null };
    setEditing(null);
    setError(null);
  };
  const closeRef = useRef(close);
  useLayoutEffect(() => {
    closeRef.current = close;
  });

  // The box and the "Link" button stay under their words while the page scrolls or the window
  // resizes; the tooltip just goes (the next hover brings it back).
  const open = Boolean(editing || hover || selectionAt);
  useEffect(() => {
    if (!open) return;
    const follow = () => {
      setHover(null);
      const sel = window.getSelection();
      const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      const rect = range && !range.collapsed && root.current?.contains(range.commonAncestorContainer) ? range.getBoundingClientRect?.() : null;
      setSelectionAt((at) => (at && rect ? below(rect) : null));
      const words = target.current.a || target.current.pending;
      if (!words) return;
      if (!words.isConnected) return closeRef.current();
      const at = below(words.getBoundingClientRect(), BOX_WIDTH);
      setEditing((e) => (e ? { ...e, at } : e));
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  // Escape or a click outside the box closes it, as Cancel does.
  const isEditing = Boolean(editing);
  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    const onDown = (e: MouseEvent) => {
      if (!dialog.current?.contains(e.target as Node)) closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [isEditing]);

  const done = () => {
    const el = root.current;
    if (!el || !editing) return;
    const href = normaliseHref(editing.href);
    const words = editing.text.replace(/\s+/g, ' ').trim();
    if (!href) return setError('Enter a web address, e.g. https://calendly.com/…');
    if (!words) return setError('Enter the words the link sits on.');
    const { a, pending } = target.current;
    if (a) {
      a.textContent = words;
      a.href = href;
      a.setAttribute('data-href', href);
      a.title = href;
    } else if (pending?.isConnected) {
      pending.replaceWith(makeLink(el.ownerDocument, words, href));
      el.normalize();
    }
    target.current = { a: null, pending: null };
    setEditing(null);
    setError(null);
    emit();
  };

  const remove = () => {
    const { a, pending } = target.current;
    if (a) unwrapLink(a);
    else if (pending?.isConnected) clearPending(pending);
    target.current = { a: null, pending: null };
    setEditing(null);
    emit();
  };

  /** Pasted text arrives as plain text: a pasted web page must not bring its markup along. */
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const sel = window.getSelection();
    if (!text || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    root.current?.normalize();
    emit();
  };

  const keep = (e: ReactMouseEvent) => e.preventDefault();

  return (
    <div className="relative">
      <div
        ref={root}
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        aria-disabled={disabled || undefined}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        onInput={emit}
        onPaste={onPaste}
        onClick={onClick}
        onMouseOver={onOver}
        onMouseLeave={() => setHover(null)}
        style={{ minHeight: `calc(${minRows} * 1.6em + 1.5rem)` }}
        className={`block w-full whitespace-pre-wrap break-words px-4 py-3 text-[14px] leading-[1.6] text-[#1A1A1A] outline-none aria-disabled:text-[#707070] ${LINK_LOOK}`}
      />
      {hover && !editing && createPortal(
        <div role="tooltip" style={hover.at} className={`pointer-events-none ${LAYER} max-w-[420px] truncate rounded-md bg-[#1A1A1A] px-2 py-1 text-xs text-white shadow`}>
          {hover.href} · click to edit
        </div>,
        document.body
      )}
      {selectionAt && !editing && !disabled && createPortal(
        <button
          type="button"
          style={selectionAt}
          onMouseDown={keep}
          onClick={newLink}
          className={`${LAYER} h-7 rounded-md border border-solid border-[#0A66C2] bg-white px-2.5 text-xs font-semibold text-[#0A66C2] shadow hover:bg-[#EEF3F8]`}
        >
          Link
        </button>,
        document.body
      )}
      {editing && createPortal(
        <div ref={dialog} role="dialog" aria-label="Edit link" style={editing.at} className={`${LAYER} w-[320px] space-y-2 rounded-md border border-solid border-[#E9EAEC] bg-white p-3 shadow-lg`}>
          <label className="block text-xs text-[#707070]">
            Text
            <input
              className="mt-0.5 block h-8 w-full rounded-md border border-solid border-[#E9EAEC] px-2 text-[13px] text-[#1A1A1A]"
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            />
          </label>
          <label className="block text-xs text-[#707070]">
            Link
            <input
              className="mt-0.5 block h-8 w-full rounded-md border border-solid border-[#E9EAEC] px-2 text-[13px] text-[#1A1A1A]"
              value={editing.href}
              placeholder="https://…"
              autoFocus
              onChange={(e) => setEditing({ ...editing, href: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  done();
                }
              }}
            />
          </label>
          {error && <p className="text-xs text-[#B3261E]">{error}</p>}
          <div className="flex justify-between gap-2">
            <button type="button" onClick={remove} className="text-xs font-semibold text-[#B3261E] hover:underline">
              {editing.existing ? 'Remove link' : 'Cancel'}
            </button>
            <span className="flex gap-2">
              {editing.existing && (
                <button type="button" onClick={close} className="h-7 rounded-md px-2.5 text-xs font-semibold text-[#707070] hover:bg-[#F8F8F8]">
                  Cancel
                </button>
              )}
              <button type="button" onClick={done} className="h-7 rounded-md bg-[#0A66C2] px-3 text-xs font-semibold text-white hover:bg-[#004182]">
                Done
              </button>
            </span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/** Read-only text with its links shown as links (sample draft, sent InMails). */
export function LinkedText({ text, className }: { text: string; className?: string }) {
  const parts: Array<string | { words: string; href: string }> = [];
  let last = 0;
  for (const m of `${text || ''}`.matchAll(/\[([^\]\n]+)\]\(([^)\s]+)\)/g)) {
    const at = m.index ?? 0;
    if (at > last) parts.push(text.slice(last, at));
    parts.push(ALLOWED_HREF.test(m[2]) ? { words: m[1], href: m[2] } : m[0]);
    last = at + m[0].length;
  }
  if (last < `${text || ''}`.length) parts.push(text.slice(last));
  return (
    <span className={`whitespace-pre-wrap ${className || ''}`}>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          p
        ) : (
          <a key={i} href={p.href} title={p.href} target="_blank" rel="noreferrer" className="font-semibold text-[#0A66C2] no-underline hover:underline">
            {p.words}
          </a>
        )
      )}
    </span>
  );
}
